import { ReactNode, createContext, useCallback, useContext } from 'react';

import { Sport } from '__generated__/globalTypes';
import { useCurrentUserContext } from 'contexts/currentUser';
import useLocalStorage from 'hooks/useLocalStorage';

import { useLocalStorageKey } from './useLocalStorageKey';

const CardSwapContext = createContext<{
  sport: Sport;
  nextStep: () => void;
  setTradeId: (tradeId: string) => void;
  setCardToSwap: (cardSlug: string) => void;
  setCardToReceive: (optionId: string) => void;
  clearCardSwapState: () => void;
  step: number;
  tradeId: string | undefined;
  cardToSwap: string | undefined;
  cardIdToReceive: string | undefined;
} | null>(null);

type CardSwapState = {
  step: number;
  tradeId: string | undefined;
  cardToSwap: string | undefined;
  cardIdToReceive: string | undefined;
};

type Props = {
  sport: Sport;
  nbSteps: number;
};
export const useCardSwapState = ({ sport, nbSteps }: Props) => {
  const { currentUser } = useCurrentUserContext();

  const KEY = useLocalStorageKey(sport, currentUser!.id);
  const [cardSwapState, setCardSwapState] = useLocalStorage<CardSwapState>(
    KEY,
    {
      step: 0,
      tradeId: undefined,
      cardToSwap: undefined,
      cardIdToReceive: undefined,
    }
  );

  const clearCardSwapState = useCallback(() => {
    setCardSwapState({
      step: 0,
      tradeId: undefined,
      cardToSwap: undefined,
      cardIdToReceive: undefined,
    });
    localStorage.removeItem(KEY);
  }, [KEY, setCardSwapState]);

  const nextStep = useCallback(() => {
    if (cardSwapState.step >= nbSteps - 1) {
      clearCardSwapState();
      return;
    }
    setCardSwapState(previousState => {
      return {
        ...previousState,
        focusedCardSlug: undefined,
        step: previousState.step + 1,
      };
    });
  }, [clearCardSwapState, nbSteps, cardSwapState.step, setCardSwapState]);

  const setTradeId = useCallback(
    (tradeId: string) => {
      setCardSwapState(previousState => {
        return {
          ...previousState,
          tradeId,
        };
      });
    },
    [setCardSwapState]
  );

  const setCardToSwap = useCallback(
    (cardToSwap: string) => {
      setCardSwapState(previousState => {
        return {
          ...previousState,
          cardToSwap,
        };
      });
    },
    [setCardSwapState]
  );

  const setCardToReceive = useCallback(
    (cardIdToReceive: string) => {
      setCardSwapState(previousState => {
        return {
          ...previousState,
          cardIdToReceive,
        };
      });
    },
    [setCardSwapState]
  );

  return {
    ...cardSwapState,
    sport,
    nextStep,
    nbSteps,
    setTradeId,
    setCardToSwap,
    setCardToReceive,
    clearCardSwapState,
  };
};

export const useCardSwapContext = () => useContext(CardSwapContext)!;

export const CardSwapContextProvider = ({
  sport,
  nbSteps,
  children,
}: {
  sport: Sport;
  nbSteps: number;
  children: ReactNode;
}) => {
  return (
    <CardSwapContext.Provider value={useCardSwapState({ sport, nbSteps })}>
      {children}
    </CardSwapContext.Provider>
  );
};
